import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { useAuth } from './AuthContext';
import { mailboxService } from '../services/mailboxService';

const API_URL = process.env.REACT_APP_WEBMAIL_API_URL || 'http://localhost:5002';

const SyncContext = createContext();

export function SyncProvider({ children }) {
  const [syncProgress, setSyncProgress] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  // Bumped whenever a sync completes successfully. Anything that displays
  // a server-side list (Inbox, sidebar folder unread counts) subscribes to
  // this and reloads when it changes.
  const [syncCompletedTick, setSyncCompletedTick] = useState(0);
  const [lastCompletedMailboxId, setLastCompletedMailboxId] = useState(null);
  // Subtle indicator for background/periodic syncs (no banner, just a dot)
  const [backgroundSyncing, setBackgroundSyncing] = useState(false);
  const connectionRef = useRef(null);
  const subscribedMailboxRef = useRef(null);
  const activeMailboxRef = useRef(null);
  // Tracks whether the user manually triggered the sync. Background/periodic
  // syncs should NOT show the full banner — only bump the completed tick.
  const manualSyncRef = useRef(false);
  const { user, loading } = useAuth();

  // Hub is [Authorize] — only negotiate after we have a stored Platform.Auth JWT.
  useEffect(() => {
    if (loading) return undefined;

    const token = localStorage.getItem('webmail_token');
    if (!user || !token) {
      connectionRef.current?.stop().catch(() => {});
      connectionRef.current = null;
      subscribedMailboxRef.current = null;
      setIsConnected(false);
      return undefined;
    }

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${API_URL}/hubs/sync-progress`, {
        withCredentials: true,
        accessTokenFactory: () => localStorage.getItem('webmail_token') || '',
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connectionRef.current = connection;

    connection.on('SyncProgress', (data) => {
      // Background syncs: silently bump the tick so inbox reloads, but DON'T
      // show the full progress banner (user feedback: annoying every 3 min).
      // Only manual syncs (triggered via startSync) show the banner.
      if (data && data.status === 'completed') {
        setLastCompletedMailboxId(data.mailboxId || activeMailboxRef.current);
        setSyncCompletedTick((t) => t + 1);
        setBackgroundSyncing(false);
        if (manualSyncRef.current) {
          setSyncProgress(data);
          setTimeout(() => {
            setSyncProgress((cur) => cur?.status === 'completed' ? null : cur);
          }, 4000);
        }
        manualSyncRef.current = false;
      } else if (data && data.status === 'error') {
        setBackgroundSyncing(false);
        if (manualSyncRef.current) {
          setSyncProgress(data);
          manualSyncRef.current = false;
        }
      } else if (manualSyncRef.current) {
        setSyncProgress(data);
      } else {
        // Background sync in progress — subtle indicator only
        setBackgroundSyncing(true);
      }
    });

    connection.onreconnected(() => {
      console.log('SignalR reconnected');
      setIsConnected(true);
      if (subscribedMailboxRef.current) {
        connection.invoke('SubscribeToMailbox', subscribedMailboxRef.current).catch(console.error);
      }
    });

    connection
      .start()
      .then(() => {
        console.log('SignalR connected (global)');
        setIsConnected(true);
      })
      .catch((err) => {
        console.error('SignalR connection failed:', err);
      });

    return () => {
      connection.stop().catch(() => {});
      if (connectionRef.current === connection) {
        connectionRef.current = null;
      }
      setIsConnected(false);
    };
  }, [loading, user]);

  // Start sync for a mailbox — shows progress bar IMMEDIATELY (manual only)
  const startSync = useCallback(async (mailboxId, mailboxEmail) => {
    activeMailboxRef.current = mailboxId;
    manualSyncRef.current = true;
    setSyncProgress({
      mailboxId,
      status: 'syncing',
      currentFolder: 'Connecting...',
      totalFolders: 0,
      completedFolders: 0,
      totalMessages: 0,
      processedMessages: 0,
      insertedMessages: 0,
      progressPercent: 0,
      mailboxEmail,
    });

    if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
      await connectionRef.current.invoke('SubscribeToMailbox', mailboxId);
      subscribedMailboxRef.current = mailboxId;
    }

    return mailboxId;
  }, []);

  // Ask the backend to abort the active sync (if any). Idempotent — also
  // updates the local UI to the "Cancelled" state so the bar doesn't sit
  // on "Syncing" while the cancellation propagates.
  const cancelSync = useCallback(async () => {
    const mailboxId = activeMailboxRef.current || syncProgress?.mailboxId;
    if (!mailboxId) return;
    try {
      await mailboxService.cancelSync(mailboxId);
      setSyncProgress((p) => p ? { ...p, status: 'error', errorMessage: 'Cancelled by user' } : p);
    } catch (err) {
      console.error('Failed to cancel sync:', err);
    }
  }, [syncProgress]);

  const dismissProgress = useCallback(() => {
    setSyncProgress(null);
    if (subscribedMailboxRef.current && connectionRef.current?.state === signalR.HubConnectionState.Connected) {
      connectionRef.current.invoke('UnsubscribeFromMailbox', subscribedMailboxRef.current).catch(console.error);
      subscribedMailboxRef.current = null;
    }
  }, []);

  return (
    <SyncContext.Provider
      value={{
        syncProgress,
        isConnected,
        startSync,
        cancelSync,
        dismissProgress,
        syncCompletedTick,
        lastCompletedMailboxId,
        backgroundSyncing,
      }}
    >
      {children}
    </SyncContext.Provider>
  );
}

export function useSync() {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error('useSync must be used within a SyncProvider');
  }
  return context;
}

export default SyncContext;
